import { useEffect, useState } from 'react'

import { useMapZoom } from '@/feature/map'

import { getDownsamplingBudget } from './downsamplingMapper'
import {
  DownsamplingThresholdMap,
  useLineStringDownsamplingConfig,
} from '../hooks/useLineStringDownsamplingConfig'

/**
 * Utility for creating buffered polygons around LineString geofences
 * This creates a corridor/buffer zone around a line with rounded end caps
 */

/**
 * Uniformly downsamples an array to at most {@link maxPoints} entries,
 * always preserving the first and last element.
 */
const simplifyCoordinates = <T>(coords: T[], maxPoints: number): T[] => {
  if (coords.length <= maxPoints) return coords

  const result: T[] = [coords[0]]
  const step = (coords.length - 1) / (maxPoints - 1)
  for (let i = 1; i < maxPoints - 1; i++) {
    result.push(coords[Math.round(i * step)])
  }
  result.push(coords[coords.length - 1])
  return result
}

export interface LineStringBufferOptions {
  lineCoords: google.maps.LatLngLiteral[]
  bufferMeters: number
  thresholdMap?: DownsamplingThresholdMap
}

export interface LineStringBufferResult {
  bufferPolygon: google.maps.LatLngLiteral[]
  isValid: boolean
}

/**
 * Creates a buffered polygon around a LineString using Google Maps geometry
 * @param options - Configuration for the buffer operation
 * @returns Buffer polygon coordinates and validity status
 */
export const createLineStringBuffer = ({
  lineCoords,
  bufferMeters,
  thresholdMap,
}: LineStringBufferOptions): LineStringBufferResult => {
  // Validate inputs
  if (lineCoords.length < 2 || !window.google?.maps?.geometry?.spherical) {
    return { bufferPolygon: [], isValid: false }
  }

  // Downsample for buffer computation only — scale budget with input size
  const maxPoints = thresholdMap ? getDownsamplingBudget(lineCoords.length, thresholdMap) : 6500
  const coords = simplifyCoordinates(lineCoords, maxPoints)
  const spherical = window.google.maps.geometry.spherical
  const len = coords.length

  // Helper function to normalize degrees to 0-360 range
  function norm(d: number): number {
    return ((d % 360) + 360) % 360
  }

  // Smallest signed angle from a to b in degrees (-180..180]
  function signedDelta(a: number, b: number): number {
    const d = ((b - a + 540) % 360) - 180
    return d === -180 ? 180 : d
  }

  // Helper function to create arc segments for rounded end caps
  const makeArc = (
    center: google.maps.LatLngLiteral,
    startBearing: number,
    sweepDeg: number,
    radiusMeters: number,
    steps = 14,
  ) => {
    const out: google.maps.LatLngLiteral[] = []
    const step = sweepDeg / steps
    for (let i = 0; i <= steps; i++) {
      const b = norm(startBearing + i * step)
      out.push(spherical.computeOffset(center, radiusMeters, b).toJSON())
    }
    return out
  }

  // Single pass: compute headings, offsets, and rounded joins together
  const leftSide: google.maps.LatLngLiteral[] = []
  const rightSide: google.maps.LatLngLiteral[] = []

  for (let i = 0; i < len; i++) {
    const curr = coords[i]

    if (i === 0) {
      const hOut = spherical.computeHeading(curr, coords[1])
      leftSide.push(spherical.computeOffset(curr, bufferMeters, norm(hOut + 90)).toJSON())
      rightSide.push(spherical.computeOffset(curr, bufferMeters, norm(hOut - 90)).toJSON())
    } else if (i === len - 1) {
      const hIn = spherical.computeHeading(coords[i - 1], curr)
      leftSide.push(spherical.computeOffset(curr, bufferMeters, norm(hIn + 90)).toJSON())
      rightSide.push(spherical.computeOffset(curr, bufferMeters, norm(hIn - 90)).toJSON())
    } else {
      const hIn = spherical.computeHeading(coords[i - 1], curr)
      const hOut = spherical.computeHeading(curr, coords[i + 1])
      const bLStart = norm(hIn + 90)
      const bLEnd = norm(hOut + 90)
      const bRStart = norm(hIn - 90)
      const bREnd = norm(hOut - 90)
      const sweepL = signedDelta(bLStart, bLEnd)
      const sweepR = signedDelta(bRStart, bREnd)
      const stepsL = Math.max(2, Math.ceil(Math.abs(sweepL) / 12))
      const stepsR = Math.max(2, Math.ceil(Math.abs(sweepR) / 12))
      leftSide.push(...makeArc(curr, bLStart, sweepL, bufferMeters, stepsL))
      rightSide.push(...makeArc(curr, bRStart, sweepR, bufferMeters, stepsR))
    }
  }

  // End caps
  const h0 = spherical.computeHeading(coords[0], coords[1])
  const hN = spherical.computeHeading(coords[len - 2], coords[len - 1])
  const endCapCW = makeArc(coords[len - 1], norm(hN + 90), -180, bufferMeters)
  const startCapCW = makeArc(coords[0], norm(h0 - 90), -180, bufferMeters)

  const ring = [...leftSide, ...endCapCW, ...rightSide.reverse(), ...startCapCW]

  // Enforce consistent winding (clockwise)
  const signedArea = (pts: google.maps.LatLngLiteral[]) => {
    let a = 0
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      a += (pts[j].lng + pts[i].lng) * (pts[j].lat - pts[i].lat)
    }
    return a
  }
  const path = signedArea(ring) > 0 ? ring.reverse() : ring

  // Remove duplicate vertices (1cm precision) and ensure closure
  const deduped: google.maps.LatLngLiteral[] = []
  let prev: google.maps.LatLngLiteral | undefined
  for (const p of path) {
    if (!prev || Math.abs(p.lat - prev.lat) > 1e-8 || Math.abs(p.lng - prev.lng) > 1e-8) {
      deduped.push(p)
      prev = p
    }
  }

  if (
    deduped.length > 1 &&
    (Math.abs(deduped[0].lat - deduped[deduped.length - 1].lat) > 1e-8 ||
      Math.abs(deduped[0].lng - deduped[deduped.length - 1].lng) > 1e-8)
  ) {
    deduped.push(deduped[0])
  }

  return {
    bufferPolygon: deduped,
    isValid: deduped.length > 0,
  }
}

/**
 * Defers buffer computation so the polyline renders immediately.
 * Skips buffer entirely when zoom is below {@link minZoom} (default 12).
 */
const EMPTY_BUFFER: google.maps.LatLngLiteral[] = []

export const useDeferredLineStringBuffer = (
  lineCoords: google.maps.LatLngLiteral[],
  bufferMeters: number,
  map: google.maps.Map | null | undefined,
  // The higher the number, the more zoomed in you need to be to see the linestring buffer
  minZoom = 12.5,
): google.maps.LatLngLiteral[] => {
  const [bufferPolygon, setBufferPolygon] = useState<google.maps.LatLngLiteral[]>(EMPTY_BUFFER)

  const zoom = useMapZoom(map)
  const showBuffer = zoom >= minZoom

  const thresholdMap = useLineStringDownsamplingConfig()

  // Deferred computation — yields to the event loop so polylines paint first
  useEffect(() => {
    if (lineCoords.length < 2 || !showBuffer) {
      setBufferPolygon(EMPTY_BUFFER)
      return
    }

    const handle = setTimeout(() => {
      const { bufferPolygon } = createLineStringBuffer({ lineCoords, bufferMeters, thresholdMap })
      setBufferPolygon(bufferPolygon)
    }, 0)

    return () => clearTimeout(handle)
  }, [lineCoords, bufferMeters, showBuffer, thresholdMap])

  return bufferPolygon
}
